import axios from 'axios';
import _ from 'lodash';

import { CONSTANT } from './constant';

const accessKey = 'accessToken';
const profile = 'userProfile';
const isPasswordUpdateRequired = 'changePassword';
const isSSOLogin = 'isSSOLogin';
const {
  KEYS: {
    ROLES: {
      ADMIN,
      SALES_REP,
      SALES_MANAGER,
      ORDER_FULFILLMENT,
      PROPOSAL_HOUSE,
      SALES_SUPPORT
    }
  },
  ROLES
} = CONSTANT;

export const environments = [
  {
    name: 'dev',
    env: 'https://d37bvvg1fq5zck.cloudfront.net',
    apiHost: 'https://d25gf1ulgvfr5j.cloudfront.net/api',
    // apiHost: 'http://localhost:3010/api',
    sof2ApiHost: 'https://dh90q9pb01dyh.cloudfront.net/api',
    adtApiHost: 'https://d3qv35y8tvum1q.cloudfront.net',
    emergencyFormsUrl: 'https://dev.mysmbforms.com'
  },
  {
    name: 'qa',
    env: 'https://qa.mysofportal.com',
    apiHost: 'https://di2fgse2jo9jy.cloudfront.net/api',
    sof2ApiHost: 'https://d1meidkmedcrj5.cloudfront.net/api',
    adtApiHost: 'https://d29z4n2gpqoeqt.cloudfront.net',
    emergencyFormsUrl: 'https://qa.mysmbforms.com'
  },
  {
    name: 'prod',
    env: 'https://www.mysofportal.com',
    apiHost: ' https://d392300n61qa5s.cloudfront.net/api',
    sof2ApiHost: 'https://d36u5buucu9rtz.cloudfront.net/api',
    adtApiHost: 'https://d2mcrq783vsnc2.cloudfront.net',
    emergencyFormsUrl: 'https://www.mysmbforms.com'
  },
  {
    name: 'preview',
    env: 'https://preview.mysofportal.com',
    apiHost: ' https://d392300n61qa5s.cloudfront.net/api',
    sof2ApiHost: 'https://d36u5buucu9rtz.cloudfront.net/api',
    adtApiHost: 'https://d2mcrq783vsnc2.cloudfront.net'
  }
];

export const getEnvironment = () => {
  const currentEnvironment = environments.find(
    environment => window.location.origin === environment.env
  );
  return currentEnvironment || environments[0];
};

const getApiHost = () => {
  const currentEnvironment = getEnvironment();
  return currentEnvironment.apiHost;
};

const getSOF2ApiHost = () => {
  const currentEnvironment = getEnvironment();
  return currentEnvironment.sof2ApiHost;
};

const getAdtDashboardApiHost = () => {
  const currentEnvironment = getEnvironment();
  return currentEnvironment.adtApiHost;
};

const getEmergencyFormsUrl = () => {
  const currentEnvironment = getEnvironment();
  return currentEnvironment.emergencyFormsUrl;
};

export const apiHost = getApiHost();
export const sof2ApiHost = getSOF2ApiHost();
export const adtApiHost = getAdtDashboardApiHost();
export const emergencyFormsUrl = getEmergencyFormsUrl();

export const config = () => {
  return {
    headers: {
      Authorization: `Bearer ${localStorage.getItem(accessKey)}`,
      'Access-Control-Allow-Origin': '*'
    }
  };
};

const mergeConfig = (options = {}) => {
  const base = config();
  return { ...base, ...options, headers: { ...base.headers, ...options.headers } };
};

axios.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.response && error.response.status === 403) {
      // Is the user already logged in?
      if (localStorage.getItem(accessKey)) {
        localStorage.setItem('isExpired', 'true');
        localStorage.removeItem('tokenExpiry');
      }
    }
    throw error;
  }
);

const request = {
  setToken: token => {
    localStorage.setItem(accessKey, token);
    // Store token expiry time (24 hours from now, matching backend token expiration)
    const tokenExpiry = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem('tokenExpiry', tokenExpiry.toString());
  },
  setProfile: value => {
    // This is a workaround for the Telus account
    // This account should have access to both SOF1 and TTT portals.
    const users = ['lori.potter@telus.com', 'trevor.smith@telus.com'];
    const workaround = {};

    if (users.includes(value.email)) {
      workaround.role_id = ROLES.SALES_SUPPORT;
      workaround.role = {
        id: ROLES.SALES_SUPPORT,
        name: SALES_SUPPORT,
        description: SALES_SUPPORT
      };
    }

    const data = {
      ...value,
      ...workaround,
      name: `${value.first_name} ${value.last_name}`
    };
    localStorage.setItem(profile, JSON.stringify(data));
  },
  setPasswordUpdate: value => {
    localStorage.setItem(isPasswordUpdateRequired, value);
  },
  setSSOLoginState: value => {
    localStorage.setItem(isSSOLogin, value);
  },
  getToken: () => {
    return localStorage.getItem(accessKey);
  },
  getProfile: () => {
    return JSON.parse(localStorage.getItem(profile));
  },
  getPasswordUpdate: () => {
    return JSON.parse(localStorage.getItem(isPasswordUpdateRequired));
  },
  getSSOLoginState: () => {
    const data = localStorage.getItem(isSSOLogin);
    return data ? JSON.parse(data) : false;
  },
  // `options` is optional axios config (e.g. { params, responseType, headers }).
  get: (url, options) => {
    return axios.get(`${apiHost}${url}`, mergeConfig(options));
  },
  post: (url, body, options) => {
    return axios.post(`${apiHost}${url}`, body, mergeConfig(options));
  },
  put: (url, body, options) => {
    return axios.put(`${apiHost}${url}`, body, mergeConfig(options));
  },
  delete: url => {
    return axios.delete(`${apiHost}${url}`, config());
  }
};

export const USER_RIGHTS = {
  ADMIN: {
    isReportAnIssueVisible: true,
    isHelpLinkVisible: true,
    isStatusEditable: true,
    isUserEditable: true,
    canAssignOrder: true,
    isAdminBandVisible: true,
    isUserPageVisible: true,
    isSOVDashboardVisible: false,
    isQuoteApprovalDashboardVisible: false,
    isBulkUploadVisible: true,
    isHomeLinkVisible: true,
    canAddComments: true,
    canPerformAction: true,
    isDefectManagementVisible: true,
    isManageControlVisible: true,
    isProductCatalogVisible: true,
    isBlackFridayReportsVisible: true
  },
  SALES_REP: {
    isReportAnIssueVisible: true,
    isHelpLinkVisible: true,
    isStatusEditable: false,
    isUserEditable: false,
    canAssignOrder: false,
    isAdminBandVisible: false,
    isUserPageVisible: false,
    isSOVDashboardVisible: false,
    isQuoteApprovalDashboardVisible: false,
    isBulkUploadVisible: false,
    isHomeLinkVisible: true,
    canAddComments: true,
    canPerformAction: false,
    isDefectManagementVisible: false,
    isManageControlVisible: false
  },
  ORDER_FULFILLMENT: {
    isReportAnIssueVisible: true,
    isHelpLinkVisible: true,
    isStatusEditable: true,
    isUserEditable: false,
    canAssignOrder: true,
    isAdminBandVisible: false,
    isUserPageVisible: false,
    isSOVDashboardVisible: false,
    isQuoteApprovalDashboardVisible: false,
    isBulkUploadVisible: false,
    isHomeLinkVisible: true,
    canAddComments: true,
    canPerformAction: true,
    isDefectManagementVisible: false,
    isManageControlVisible: false
  },
  PROPOSAL_HOUSE: {
    isReportAnIssueVisible: true,
    isHelpLinkVisible: true,
    isStatusEditable: false,
    isUserEditable: false,
    canAssignOrder: false,
    isAdminBandVisible: false,
    isUserPageVisible: false,
    isSOVDashboardVisible: false,
    isQuoteApprovalDashboardVisible: false,
    isBulkUploadVisible: false,
    isHomeLinkVisible: true,
    canAddComments: false,
    canPerformAction: false,
    isDefectManagementVisible: false,
    isManageControlVisible: false
  },
  SALES_MANAGER: {
    isReportAnIssueVisible: true,
    isHelpLinkVisible: true,
    isStatusEditable: false,
    isUserEditable: false,
    canAssignOrder: false,
    isAdminBandVisible: false,
    isUserPageVisible: false,
    isSOVDashboardVisible: false,
    isQuoteApprovalDashboardVisible: false,
    isBulkUploadVisible: false,
    isHomeLinkVisible: true,
    canAddComments: false,
    canPerformAction: false,
    isDefectManagementVisible: false,
    isManageControlVisible: false
  },
  SALES_SUPPORT: {
    isReportAnIssueVisible: true,
    isHelpLinkVisible: true,
    isStatusEditable: false,
    isUserEditable: false,
    canAssignOrder: false,
    isAdminBandVisible: false,
    isUserPageVisible: false,
    isSOVDashboardVisible: false,
    isQuoteApprovalDashboardVisible: false,
    isBulkUploadVisible: false,
    isHomeLinkVisible: true,
    canAddComments: false,
    canPerformAction: false,
    isDefectManagementVisible: false,
    isManageControlVisible: false,
    isProductCatalogVisible: false
  },
  ADMIN_TIME_TRACKING: {
    isReportAnIssueVisible: false,
    isHelpLinkVisible: false,
    isStatusEditable: false,
    isUserEditable: false,
    canAssignOrder: false,
    isAdminBandVisible: false,
    isUserPageVisible: false,
    isSOVDashboardVisible: false,
    isQuoteApprovalDashboardVisible: false,
    isBulkUploadVisible: false,
    isHomeLinkVisible: true,
    canAddComments: false,
    canPerformAction: false,
    isDefectManagementVisible: false,
    isManageControlVisible: false,
    isProductCatalogVisible: false
  },
  MANAGER_TIME_TRACKING: {
    isReportAnIssueVisible: false,
    isHelpLinkVisible: false,
    isStatusEditable: false,
    isUserEditable: false,
    canAssignOrder: false,
    isAdminBandVisible: false,
    isUserPageVisible: false,
    isSOVDashboardVisible: false,
    isQuoteApprovalDashboardVisible: false,
    isBulkUploadVisible: false,
    isHomeLinkVisible: true,
    canAddComments: false,
    canPerformAction: false,
    isDefectManagementVisible: false,
    isManageControlVisible: false,
    isProductCatalogVisible: false
  },
  TEAM_MEMBER_TIME_TRACKING: {
    isReportAnIssueVisible: false,
    isHelpLinkVisible: false,
    isStatusEditable: false,
    isUserEditable: false,
    canAssignOrder: false,
    isAdminBandVisible: false,
    isUserPageVisible: false,
    isSOVDashboardVisible: false,
    isQuoteApprovalDashboardVisible: false,
    isBulkUploadVisible: false,
    isHomeLinkVisible: true,
    canAddComments: false,
    canPerformAction: false,
    isDefectManagementVisible: false,
    isManageControlVisible: false,
    isProductCatalogVisible: false
  }
};

const roleId = _.get(request.getProfile(), 'role.name', SALES_REP);

export const isAdmin = roleId === ADMIN;

export const isSalesRep = roleId === SALES_REP;

export const isOrderFulfillmentTeam = roleId === ORDER_FULFILLMENT;

export const isSalesManager = roleId === SALES_MANAGER;

export const isProposalHouse = roleId === PROPOSAL_HOUSE;

export const isSalesSupport = roleId === SALES_SUPPORT;

// SOF-3356 T009 — derived order-submit permission for the current profile.
// Only meaningful inside `isSalesManager`-true branches per Clarifications
// 2026-04-28; backend omits the field for non-SM roles, so default is false.
//
// Transition safety (post-deploy stale-localStorage fix): if the cached
// profile predates the SOF-3356 backend deploy it will not contain the new
// `requiresOrderAccess` field at all. In that case treat `canSubmitOrders` as
// permissive (`true`) — the authoritative enforcement is the backend
// `requires_order_access`-aware mutation gate (FR-B024), so the FE remaining
// permissive for legacy profiles only relaxes UI tile visibility, never the
// real submit boundary. Once the SM logs out and back in, the fresh profile
// carries the explicit boolean and this branch no longer fires.
export const canSubmitOrders = () => {
  const currentProfile = request.getProfile();
  const explicit = _.get(currentProfile, 'canSubmitOrders');
  if (typeof explicit === 'boolean') return explicit;
  const hasNewShape =
    currentProfile &&
    Object.prototype.hasOwnProperty.call(currentProfile, 'requiresOrderAccess');
  return !hasNewShape;
};

export default request;
